import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const AlbumCreator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [albumTitle, setAlbumTitle] = useState('My Family Photo Album');
  const [albumDescription, setAlbumDescription] = useState('');
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photoDetails, setPhotoDetails] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [albumStyle, setAlbumStyle] = useState('classic');
  const [includeMetadata, setIncludeMetadata] = useState(true);

  useEffect(() => {
    // Get selected images from navigation state
    if (location.state?.selectedImages) {
      fetchSelectedPhotos(location.state.selectedImages);
    } else {
      // If no images selected, redirect back to gallery
      navigate('/gallery');
    }
  }, [location.state, navigate]);

  const fetchSelectedPhotos = async (imageIds) => {
    try {
      const token = localStorage.getItem('token');
      const photoPromises = imageIds.map(id => 
        axios.get(`/api/documents/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      );
      
      const responses = await Promise.all(photoPromises);
      const photos = responses.map(res => res.data);
      setSelectedPhotos(photos);
      
      // Initialize photo details
      const details = {};
      photos.forEach(photo => {
        details[photo._id] = {
          caption: photo.originalName.replace(/\.[^/.]+$/, ''),
          description: '',
          includeInAlbum: true,
          order: photos.indexOf(photo)
        };
      });
      setPhotoDetails(details);
    } catch (error) {
      console.error('Error fetching photos:', error);
      alert('Failed to load selected photos');
    }
  };

  const updatePhotoDetail = (photoId, field, value) => {
    setPhotoDetails(prev => ({
      ...prev,
      [photoId]: {
        ...prev[photoId],
        [field]: value
      }
    }));
  };

  const movePhoto = (photoId, direction) => {
    const currentOrder = photoDetails[photoId].order;
    const newOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1;
    
    if (newOrder < 0 || newOrder >= selectedPhotos.length) return;
    
    // Swap orders
    const otherPhotoId = selectedPhotos.find(p => photoDetails[p._id].order === newOrder)?._id;
    if (otherPhotoId) {
      setPhotoDetails(prev => ({
        ...prev,
        [photoId]: { ...prev[photoId], order: newOrder },
        [otherPhotoId]: { ...prev[otherPhotoId], order: currentOrder }
      }));
    }
  };

  const generateAlbum = async () => {
    setIsGenerating(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Prepare album data
      const albumData = {
        title: albumTitle,
        description: albumDescription,
        style: albumStyle,
        includeMetadata,
        photos: selectedPhotos
          .filter(photo => photoDetails[photo._id].includeInAlbum)
          .sort((a, b) => photoDetails[a._id].order - photoDetails[b._id].order)
          .map(photo => ({
            id: photo._id,
            caption: photoDetails[photo._id].caption,
            description: photoDetails[photo._id].description,
            people: photo.peopleIdentified || [],
            date: photo.dateInfo,
            location: photo.geographicalInfo
          }))
      };
      
      // Call backend to generate PDF
      const response = await axios.post('/api/albums/generate', albumData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob'
      });
      
      // Download the PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${albumTitle.replace(/[^a-z0-9]/gi, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('Album generated successfully!');
      navigate('/gallery');
      
    } catch (error) {
      console.error('Error generating album:', error);
      alert('Failed to generate album. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const sortedPhotos = [...selectedPhotos].sort((a, b) => 
    photoDetails[a._id]?.order - photoDetails[b._id]?.order
  );

  const includedPhotos = sortedPhotos.filter(photo => 
    photoDetails[photo._id]?.includeInAlbum
  );

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
      <h1>Create Photo Album</h1>
      
      {/* Album Settings */}
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2>Album Settings</h2>
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Album Title
          </label>
          <input
            type="text"
            value={albumTitle}
            onChange={(e) => setAlbumTitle(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '16px'
            }}
          />
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Album Description
          </label>
          <textarea
            value={albumDescription}
            onChange={(e) => setAlbumDescription(e.target.value)}
            placeholder="Add a description for your album..."
            rows="3"
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </div>
        
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Album Style
            </label>
            <select
              value={albumStyle}
              onChange={(e) => setAlbumStyle(e.target.value)}
              style={{
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            >
              <option value="classic">Classic</option>
              <option value="modern">Modern</option>
              <option value="vintage">Vintage</option>
              <option value="minimal">Minimal</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={includeMetadata}
                onChange={(e) => setIncludeMetadata(e.target.checked)}
              />
              <span style={{ fontWeight: 'bold' }}>Include photo metadata (dates, people, locations)</span>
            </label>
          </div>
        </div>
      </div>
      
      {/* Photo Management */}
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2>Photos ({includedPhotos.length} of {selectedPhotos.length})</h2>
        <p style={{ color: '#666', marginBottom: '1rem' }}>
          Drag to reorder, add captions, or exclude photos from your album
        </p>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {sortedPhotos.map((photo, index) => (
            <div
              key={photo._id}
              style={{
                display: 'flex',
                gap: '1rem',
                padding: '1rem',
                border: '1px solid #ddd',
                borderRadius: '8px',
                background: photoDetails[photo._id]?.includeInAlbum ? 'white' : '#f8f9fa',
                opacity: photoDetails[photo._id]?.includeInAlbum ? 1 : 0.6
              }}
            >
              {/* Photo Thumbnail */}
              <div style={{ flexShrink: 0 }}>
                <img
                  src={`/api/documents/${photo._id}/view?token=${localStorage.getItem('token')}`}
                  alt={photo.originalName}
                  style={{
                    width: '150px',
                    height: '150px',
                    objectFit: 'cover',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              {/* Photo Details */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={photoDetails[photo._id]?.includeInAlbum || false}
                    onChange={(e) => updatePhotoDetail(photo._id, 'includeInAlbum', e.target.checked)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <strong>Include in album</strong>
                </div>
                
                {photoDetails[photo._id]?.includeInAlbum && (
                  <>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <label style={{ display: 'block', fontSize: '14px', color: '#666' }}>
                        Caption
                      </label>
                      <input
                        type="text"
                        value={photoDetails[photo._id]?.caption || ''}
                        onChange={(e) => updatePhotoDetail(photo._id, 'caption', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.25rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      />
                    </div>
                    
                    <div style={{ marginBottom: '0.5rem' }}>
                      <label style={{ display: 'block', fontSize: '14px', color: '#666' }}>
                        Description
                      </label>
                      <textarea
                        value={photoDetails[photo._id]?.description || ''}
                        onChange={(e) => updatePhotoDetail(photo._id, 'description', e.target.value)}
                        placeholder="Add details about this photo..."
                        rows="2"
                        style={{
                          width: '100%',
                          padding: '0.25rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    
                    {photo.peopleIdentified && photo.peopleIdentified.length > 0 && (
                      <p style={{ fontSize: '12px', color: '#666' }}>
                        People: {photo.peopleIdentified.map(p => p.name).join(', ')}
                      </p>
                    )}
                  </>
                )}
              </div>
              
              {/* Reorder Buttons */}
              {photoDetails[photo._id]?.includeInAlbum && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <button
                    onClick={() => movePhoto(photo._id, 'up')}
                    disabled={index === 0}
                    style={{
                      padding: '0.25rem 0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      background: 'white',
                      cursor: index === 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => movePhoto(photo._id, 'down')}
                    disabled={index === sortedPhotos.length - 1}
                    style={{
                      padding: '0.25rem 0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      background: 'white',
                      cursor: index === sortedPhotos.length - 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    ↓
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        justifyContent: 'space-between'
      }}>
        <button
          onClick={() => navigate('/gallery')}
          style={{
            padding: '0.75rem 1.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            background: 'white',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        
        <button
          onClick={generateAlbum}
          disabled={isGenerating || includedPhotos.length === 0}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '4px',
            background: includedPhotos.length === 0 ? '#ccc' : '#2e7d32',
            color: 'white',
            cursor: includedPhotos.length === 0 ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          {isGenerating ? 'Generating...' : `Generate Album (${includedPhotos.length} photos)`}
        </button>
      </div>
    </div>
  );
};

export default AlbumCreator;
